/** The onboarding-invites search namespaces (QuerySearch over Onboarding_Invite).
 *
 * A small table: no mandatory time window. `status` is the computed status; `restech` matches
 * invites whose context names that booking system (an invite without the key offers every one
 * and is not matched); `source` who issued it (`marketplace`: a marketplace app install); `batch` is the batch id one bulk create shares; `tenant` matches the
 * tenant created from a used invite. Free text matches the label, the e-mail, the public id (a
 * pasted code finds its invite by its public part) and the tenant.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (OnboardingInviteFilterNode — contract-tested).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/bookings';
import { INVITE_SOURCES, INVITE_STATUSES } from '@/api/onboardingInvites';

/** A local (no-API) suggestion fetcher from a fixed list. */
function localLabels(labels: readonly string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export interface InviteNamespaceOptions {
  /** Booking-system codes to suggest for `restech` (the easy registry, when it could be read). */
  restechs?: readonly string[];
}

export function buildInviteNamespaces(opts: InviteNamespaceOptions = {}): NamespaceConfigMap {
  return {
    status: {
      label: 'status',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(INVITE_STATUSES),
      help: 'available · claimed (a browser holds the link) · expired · used (the tenant and its admin exist; the link is dead) · live (the tenant went live) · revoked.',
      examples: ['status=available', 'status=claimed', '!status=revoked'],
    },
    label: {
      label: 'label',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'The invite’s label (contains with `=~`; `=` for an exact, case-insensitive match).',
      examples: ['label=~ITB', 'label="ACME Tours — contract Nov 2026"'],
    },
    email: {
      label: 'email',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'The bound e-mail (unbound links have none).',
      examples: ['email=~@acme.example', 'email=ops@acme.example'],
    },
    created_by: {
      label: 'created_by',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      help: 'Who created the invite: the e-mail (`=`, or contains with `=~`), or the user id with `=#`.',
      examples: ['created_by=maya.chen@curatedplanet.com', 'created_by=~maya', 'created_by=#12'],
    },
    batch: {
      label: 'batch',
      operators: ['exact'],
      valueType: 'string',
      help: 'The batch id one bulk create gave all its invites (the QR sheet’s links).',
      examples: ['batch=0b8e2f4c-1d2a-4c55-9a7e-3f0e5d7c9b11'],
    },
    restech: {
      label: 'restech',
      operators: ['iexact'],
      valueType: 'string',
      ...(opts.restechs?.length ? { fetchSuggestions: localLabels(opts.restechs) } : {}),
      help: 'Invites restricted to this booking system (context `restechs`). An invite without the restriction offers every booking system and is not matched.',
      examples: ['restech=fareharbor'],
    },
    source: {
      label: 'source',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(INVITE_SOURCES),
      help: 'Who issued the invite: console · automation · api · marketplace (minted by a booking system’s marketplace app install, with no ops step).',
      examples: ['source=marketplace', '!source=marketplace'],
    },
    promo: {
      label: 'promo',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: 'The promo code in the invite’s context.',
      examples: ['promo=ITB2027', 'promo=~ITB'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact', 'exact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'The tenant created from a used invite: its code (current, or as created), or its id with `=#`; an all-digits value also matches the id.',
      examples: ['tenant=acmetours', 'tenant=#412'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the invite was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-10-17', 'created="2026-10-01 00:00".."2026-10-17 18:00"'],
    },
    expires: {
      label: 'expires',
      operators: ['exact', 'range', 'gt', 'lt'],
      valueType: 'datetime',
      help: 'Expiry, UTC. Open bounds work — `expires=..2026-12-31` finds invites expiring before a date.',
      examples: ['expires=..2026-12-31', 'expires=2026-11-01..'],
    },
    claimed: {
      label: 'claimed',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When a browser claimed the link (Start onboarding), UTC.',
      examples: ['claimed=2026-10-17'],
    },
    sent: {
      label: 'sent',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the link was last e-mailed, UTC.',
      examples: ['sent=2026-10-17'],
    },
    used: {
      label: 'used',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the tenant and its admin account were created from the invite, UTC.',
      examples: ['used=2026-10-17'],
    },
    completed: {
      label: 'completed',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the tenant went live, UTC.',
      examples: ['completed=2026-10-17'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'string',
      help: 'The invite’s integer id (exact).',
      examples: ['id=123'],
    },
  };
}
